import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check } from "lucide-react";
import { Logo } from "../components/Bits";
import { api, errMsg } from "../lib/api";
import { useAutosave } from "../lib/useAutosave";
import { login } from "../context/auth";

const STATES = ["California", "Alabama", "Alaska", "Arizona", "Arkansas", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming", "District of Columbia"];
const YN = ["Yes", "No", "Not sure"];

const QUESTIONS = [
  { key: "care_for", q: "Who do you care for?", options: ["A parent", "My spouse or partner", "Another relative", "A friend or neighbor"] },
  { key: "age", q: "How old are they?", options: ["Under 60", "60 to 74", "75 or older"] },
  { key: "dementia", q: "Do they have dementia or memory loss?", options: YN, hint: "A diagnosis or just signs you've noticed. Both count here." },
  { key: "medicare", q: "Do they have Medicare?", options: YN },
  { key: "medi_cal", q: "Do they have Medi-Cal?", options: YN, hint: "Medi-Cal is California's Medicaid program." },
  { key: "veteran", q: "Are they a veteran?", options: YN },
  { key: "location", q: "Where do they live?", location: true },
  { key: "hours", q: "About how many hours of care do you give each week?", options: ["Under 10", "10 to 20", "20 to 40", "More than 40"] },
];

function Question({ step, answers, set, next }) {
  const cur = QUESTIONS[step];
  if (cur.location) {
    return (
      <div className="space-y-5">
        <label className="block text-lg font-semibold">State</label>
        <select data-testid="elig-state-select" className="hf-input" value={answers.state || "California"} onChange={(e) => set({ state: e.target.value })}>
          {STATES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <label className="block text-lg font-semibold">County</label>
        <input data-testid="elig-county-input" className="hf-input" placeholder="For example, San Mateo" value={answers.county || ""} onChange={(e) => set({ county: e.target.value })} />
        <button data-testid="elig-location-next" onClick={() => { if (!answers.state) set({ state: "California" }); next(); }} className="hf-btn hf-btn-primary w-full">
          Next
        </button>
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      {cur.options.map((o) => (
        <button
          key={o}
          data-testid={`elig-option-${cur.key}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => { set({ [cur.key]: o }); next(); }}
          className={`flex min-h-[68px] items-center justify-between rounded-2xl border-2 bg-white px-6 text-left text-xl font-medium transition-colors hover:border-hf-green ${
            answers[cur.key] === o ? "border-hf-green bg-hf-sage" : "border-hf-line"
          }`}
        >
          {o}
          {answers[cur.key] === o && <Check className="h-6 w-6 text-hf-green" />}
        </button>
      ))}
    </div>
  );
}

function Results({ results, answers, restart }) {
  const nav = useNavigate();
  const real = results.filter((r) => r.key !== "private");
  const save = () => {
    localStorage.setItem("handoff_elig_pending", JSON.stringify(answers));
    login("/app");
  };
  return (
    <div className="hf-rise">
      <p className="hf-eyebrow">Your results</p>
      <h1 data-testid="elig-result-headline" className="mt-2 font-heading text-4xl font-semibold leading-tight sm:text-5xl">
        {real.length ? "Your break may be covered." : "Let's find your options together."}
      </h1>
      <p className="mt-4 text-xl text-hf-muted">You may be eligible for:</p>
      <div className="mt-6 space-y-4">
        {results.map((r, i) => (
          <div key={r.key} data-testid={`elig-result-${r.key}`} className={`hf-card hf-rise ${r.key === "private" ? "bg-hf-creamdeep" : ""}`} style={{ animationDelay: `${i * 90}ms` }}>
            <div className="flex items-start gap-3">
              <span className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${r.key === "private" ? "bg-hf-line" : "bg-hf-green"}`}>
                <Check className={`h-5 w-5 ${r.key === "private" ? "text-hf-muted" : "text-hf-cream"}`} />
              </span>
              <div>
                <h2 className="font-heading text-xl font-semibold">{r.title}</h2>
                <p className="mt-1 text-lg leading-relaxed text-hf-muted">{r.body}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      <p data-testid="elig-disclaimer" className="mt-6 rounded-xl bg-hf-sun/25 px-5 py-4 text-base text-hf-ink">
        This is not a promise of coverage. You may be eligible, and we'll help you confirm with each program.
      </p>
      <div className="mt-8 flex flex-col gap-3">
        <button data-testid="elig-save-continue" onClick={save} className="hf-btn hf-btn-primary w-full">Save my results and continue</button>
        <button data-testid="elig-see-demo" onClick={() => nav("/demo")} className="hf-btn hf-btn-ghost w-full">See how it works first</button>
        <button data-testid="elig-restart" onClick={restart} className="min-h-[48px] text-base font-medium text-hf-muted underline">Start over</button>
      </div>
    </div>
  );
}

export default function Eligibility() {
  const [answers, setAnswers, clearAnswers] = useAutosave("handoff_elig_answers", {});
  const [step, setStep] = useAutosave("handoff_elig_step", 0);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (p) => setAnswers((a) => ({ ...a, ...p }));

  const finish = async (final) => {
    setBusy(true);
    try {
      const r = await api.post("/eligibility/check", { answers: final });
      setResults(r.data.results);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const next = () => {
    if (step + 1 >= QUESTIONS.length) return;
    setStep(step + 1);
  };
  const answerLast = (p) => {
    const final = { ...answers, ...p };
    setAnswers(final);
    finish(final);
  };
  const restart = () => {
    clearAnswers();
    setStep(0);
    setResults(null);
  };
  const cur = QUESTIONS[Math.min(step, QUESTIONS.length - 1)];
  const isLast = step === QUESTIONS.length - 1;

  return (
    <div className="min-h-screen bg-hf-cream">
      <header className="mx-auto flex max-w-xl items-center justify-between px-5 py-5">
        <Logo />
        <span className="text-sm text-hf-muted">No login needed</span>
      </header>
      <main className="mx-auto max-w-xl px-5 pb-20">
        {results ? (
          <Results results={results} answers={answers} restart={restart} />
        ) : (
          <div key={step} className="hf-rise">
            <div className="mb-8 flex items-center gap-3">
              {step > 0 ? (
                <button data-testid="elig-back" onClick={() => setStep(step - 1)} aria-label="Back" className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-hf-green">
                  <ArrowLeft className="h-5 w-5" />
                </button>
              ) : (
                <Link to="/" data-testid="elig-home" aria-label="Home" className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-hf-green">
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              )}
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-hf-line">
                <div className="h-full rounded-full bg-hf-coral transition-[width] duration-500" style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }} />
              </div>
              <span data-testid="elig-progress" className="text-base font-medium text-hf-muted">{step + 1} of {QUESTIONS.length}</span>
            </div>
            <h1 data-testid="elig-question" className="font-heading text-3xl font-semibold leading-tight sm:text-4xl">{cur.q}</h1>
            {cur.hint && <p className="mt-3 text-lg text-hf-muted">{cur.hint}</p>}
            <div className="mt-8">
              <Question step={step} answers={answers} set={isLast ? answerLast : set} next={next} />
            </div>
            {busy && <p className="mt-6 text-lg text-hf-muted">Checking what may fit...</p>}
          </div>
        )}
      </main>
    </div>
  );
}
